import { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Send, Bot, User, Trash2, AlertCircle, ThumbsUp, ThumbsDown, Copy, Check } from 'lucide-react';
import { linkifyCitations, useCitationComponents } from '../ai/citations';
import type { SdsChatMessage } from '../../types/sdsAssistant';

interface SdsChatProps {
    messages: SdsChatMessage[];
    isSending: boolean;
    error: string | null;
    onSend: (content: string) => void;
    onClear: () => void;
    /** Called when the user clicks an inline [n] marker — highlights that source on the right. */
    onCitationClick: (sourceId: number) => void;
    onFeedback: (question: string, answer: string, rating: 1 | -1) => Promise<void> | void;
    /** Highest valid [n]; markers above it stay plain text instead of linking nowhere. */
    sourceCount: number;
}

const EXAMPLE_QUESTIONS = [
    'Which products carry H317?',
    'What is the storage temperature of ELVALOY 742?',
    'Which H and P statements apply to this product?',
];

export function SdsChat({ messages, isSending, error, onSend, onClear, onCitationClick, onFeedback, sourceCount }: SdsChatProps) {
    const [input, setInput] = useState('');
    const [ratings, setRatings] = useState<Record<number, 1 | -1>>({});
    const [copiedIdx, setCopiedIdx] = useState<number | null>(null);
    const messagesEndRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, isSending]);

    // The question an assistant message answers = the nearest preceding user turn.
    const questionFor = (assistantIdx: number): string => {
        for (let i = assistantIdx - 1; i >= 0; i--) {
            if (messages[i].role === 'user') return messages[i].content;
        }
        return '';
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!input.trim() || isSending) return;
        onSend(input);
        setInput('');
    };

    const handleRate = async (idx: number, rating: 1 | -1) => {
        if (ratings[idx]) return;
        setRatings(prev => ({ ...prev, [idx]: rating }));
        try {
            await onFeedback(questionFor(idx), messages[idx].content, rating);
        } catch {
            setRatings(prev => {
                const next = { ...prev };
                delete next[idx];
                return next;
            });
        }
    };

    const handleCopy = async (idx: number) => {
        try {
            await navigator.clipboard.writeText(messages[idx].content);
            setCopiedIdx(idx);
            setTimeout(() => setCopiedIdx(null), 1500);
        } catch {
            // clipboard unavailable (insecure context) — nothing useful to do
        }
    };

    const markdownComponents = useCitationComponents(onCitationClick);

    return (
        <div className="flex h-full min-h-0 w-full flex-col overflow-hidden rounded-lg border border-gray-200 bg-white">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
                <div className="flex items-center gap-2">
                    <Bot className="h-4 w-4 text-blue-600" />
                    <span className="text-sm font-medium text-gray-700">SDS Assistant</span>
                </div>
                {messages.length > 0 && (
                    <button
                        onClick={onClear}
                        className="p-1 text-gray-400 hover:text-gray-600"
                        title="Reset chat"
                    >
                        <Trash2 className="h-4 w-4" />
                    </button>
                )}
            </div>

            {/* Messages */}
            <div className="flex-1 space-y-4 overflow-y-auto px-4 py-3">
                {messages.length === 0 && !isSending && (
                    <div className="flex h-full flex-col items-center justify-center text-center">
                        <Bot className="mb-3 h-10 w-10 text-gray-300" />
                        <p className="mb-1 text-sm text-gray-500">
                            Ask about the <strong>SDS/MSDS datasheets</strong>
                        </p>
                        <p className="mb-5 max-w-sm text-xs text-gray-400">
                            Every answer is built strictly from the retrieved datasheet extracts and
                            backed by sources. Ask in any language — the answer comes back in that same
                            language.
                        </p>
                        <div className="w-full max-w-md space-y-1.5">
                            {EXAMPLE_QUESTIONS.map(q => (
                                <button
                                    key={q}
                                    onClick={() => onSend(q)}
                                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-left text-xs text-gray-600 transition-colors hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700"
                                >
                                    {q}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {messages.map((msg, i) => (
                    <div key={i} className={`flex gap-2.5 ${msg.role === 'user' ? 'justify-end' : ''}`}>
                        {msg.role === 'assistant' && (
                            <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-blue-100">
                                <Bot className="h-4 w-4 text-blue-600" />
                            </div>
                        )}
                        <div className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${
                            msg.role === 'user' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-800'
                        }`}>
                            {msg.role === 'assistant' ? (
                                <>
                                    <div className="prose prose-sm max-w-none prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-li:my-0.5 prose-headings:my-2 prose-headings:text-gray-800 prose-table:my-1 prose-th:px-2 prose-th:py-1 prose-td:px-2 prose-td:py-1 prose-th:bg-gray-200 prose-th:text-left prose-td:border prose-td:border-gray-200 prose-th:border prose-th:border-gray-300">
                                        <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                                            {linkifyCitations(msg.content, sourceCount)}
                                        </ReactMarkdown>
                                    </div>
                                    <div className="mt-1.5 flex gap-1 border-t border-gray-200 pt-1.5">
                                        <button
                                            onClick={() => handleRate(i, 1)}
                                            disabled={!!ratings[i]}
                                            className={`flex items-center gap-1 rounded px-2 py-0.5 text-[11px] font-medium transition-colors ${
                                                ratings[i] === 1
                                                    ? 'cursor-default bg-green-50 text-green-600'
                                                    : 'text-gray-500 hover:bg-green-50 hover:text-green-600 disabled:opacity-40'
                                            }`}
                                            title="Helpful answer"
                                        >
                                            <ThumbsUp className="h-3 w-3" />
                                        </button>
                                        <button
                                            onClick={() => handleRate(i, -1)}
                                            disabled={!!ratings[i]}
                                            className={`flex items-center gap-1 rounded px-2 py-0.5 text-[11px] font-medium transition-colors ${
                                                ratings[i] === -1
                                                    ? 'cursor-default bg-red-50 text-red-600'
                                                    : 'text-gray-500 hover:bg-red-50 hover:text-red-600 disabled:opacity-40'
                                            }`}
                                            title="Not helpful"
                                        >
                                            <ThumbsDown className="h-3 w-3" />
                                        </button>
                                        <button
                                            onClick={() => handleCopy(i)}
                                            className="flex items-center gap-1 rounded px-2 py-0.5 text-[11px] font-medium text-gray-500 transition-colors hover:bg-blue-50 hover:text-blue-600"
                                            title="Copy answer"
                                        >
                                            {copiedIdx === i
                                                ? <><Check className="h-3 w-3" /> Copied</>
                                                : <><Copy className="h-3 w-3" /> Copy</>
                                            }
                                        </button>
                                    </div>
                                </>
                            ) : (
                                <div className="whitespace-pre-wrap">{msg.content}</div>
                            )}
                        </div>
                        {msg.role === 'user' && (
                            <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-gray-200">
                                <User className="h-4 w-4 text-gray-600" />
                            </div>
                        )}
                    </div>
                ))}

                {isSending && (
                    <div className="flex gap-2.5">
                        <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-blue-100">
                            <Bot className="h-4 w-4 text-blue-600" />
                        </div>
                        <div className="flex items-center gap-2 rounded-lg bg-gray-100 px-3 py-2">
                            <div className="flex gap-1">
                                <span className="h-2 w-2 animate-bounce rounded-full bg-gray-400" style={{ animationDelay: '0ms' }} />
                                <span className="h-2 w-2 animate-bounce rounded-full bg-gray-400" style={{ animationDelay: '150ms' }} />
                                <span className="h-2 w-2 animate-bounce rounded-full bg-gray-400" style={{ animationDelay: '300ms' }} />
                            </div>
                            <span className="text-xs italic text-gray-600">Searching datasheets …</span>
                        </div>
                    </div>
                )}

                {error && (
                    <div className="flex items-center gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">
                        <AlertCircle className="h-4 w-4 flex-shrink-0" />
                        {error}
                    </div>
                )}

                <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <form onSubmit={handleSubmit} className="border-t border-gray-200 px-4 py-3">
                <div className="flex gap-2">
                    <input
                        type="text"
                        value={input}
                        onChange={e => setInput(e.target.value)}
                        placeholder="Ask about a datasheet …"
                        disabled={isSending}
                        className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                    />
                    <button
                        type="submit"
                        disabled={!input.trim() || isSending}
                        className="rounded-lg bg-blue-600 px-3 py-2 text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        <Send className="h-4 w-4" />
                    </button>
                </div>
            </form>
        </div>
    );
}
